import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { Gallery, Collection, Item, GallerySummary, Collaborator } from './types'
import { resizeImage } from './imageResize'
import type { ExifData } from './exifExtract'
import { generateBlurBase64 } from './blur'
import { metricCount, metricDistribution } from './metrics'

export const STORAGE_BUCKET = 'tbd'
export const COVERS_BUCKET = 'covers'

/**
 * Build a public URL for a cover image stored in the public covers bucket.
 */
export function getCoverPublicUrl(path: string): string {
  if (!path) return ''
  const { data } = supabase.storage.from(COVERS_BUCKET).getPublicUrl(path)
  return data.publicUrl
}

// --- Auth ---

export async function signInWithGoogle(redirectTo?: string) {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo ?? `${window.location.origin}/login`,
    },
  })
  if (error) throw new Error(error.message)
}

/**
 * Called after a Google OAuth redirect completes.
 * Verifies the user has an owners row (allowlist check).
 * If not, attempts lazy creation via ensure_owner_row().
 * If still no row, signs out and throws — email is not allowlisted.
 */
export async function verifyGoogleOAuthAllowlist(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return // no session, nothing to check

  const { data: ownerData } = await supabase
    .from('owners')
    .select('id')
    .single()

  if (!ownerData) {
    const { error: rpcError } = await supabase.rpc('ensure_owner_row')
    if (rpcError) {
      await supabase.auth.signOut()
      throw new Error('Din konto er ikke autorisert. Kontakt administratoren.')
    }
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw new Error(error.message)
}

/**
 * Register a new account with email + password.
 * Anyone can call this — Supabase creates the auth.users row.
 * The DB trigger will create an owners row automatically if the email is
 * already in allowed_owners. If not, the user will be blocked at sign-in.
 * Returns { needsConfirmation: true } when Supabase sends a confirmation email.
 */
export async function signUpWithEmail(
  email: string,
  password: string,
): Promise<{ needsConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({ email, password })
  if (error) throw new Error(error.message)
  // When email confirmation is required, session is null after signUp
  const needsConfirmation = !data.session
  if (!needsConfirmation) {
    // User was signed in immediately — check allowlist before letting them through
    await verifyAllowlist()
  }
  return { needsConfirmation }
}

/**
 * Verify the current session user has an owners row (allowlist check).
 * Attempts lazy creation via ensure_owner_row() for users who registered
 * before being added to the allowlist. If still no row, signs out and throws.
 */
async function verifyAllowlist(): Promise<void> {
  const { data: ownerData } = await supabase
    .from('owners')
    .select('id')
    .maybeSingle()

  if (!ownerData) {
    const { error: rpcError } = await supabase.rpc('ensure_owner_row')
    if (rpcError) {
      await supabase.auth.signOut()
      throw new Error('Din konto er ikke autorisert. Kontakt administratoren.')
    }
  }
}

/**
 * Sign in with email + password.
 * After a successful auth sign-in we verify the user has an owners row.
 * If not, we attempt to create one via ensure_owner_row() (handles the case
 * where the user registered before being added to the allowlist).
 * If still no owners row, the email is not allowlisted — we sign out and throw.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<void> {
  const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
  if (signInError) throw new Error(signInError.message)

  await verifyAllowlist()
}

/**
 * Send a password-reset email. The link lands on /reset-password, where the
 * recovery session is picked up and the user sets a new password.
 * Does not reveal whether the email exists — Supabase returns success either way.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (error) throw new Error(error.message)
}

/**
 * Set a new password for the currently authenticated user.
 * Used on /reset-password while holding a recovery session.
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) throw new Error(error.message)
}

// Cache admin status for the lifetime of the session — it never changes mid-session
// and making a round-trip on every page load causes visible UI jumpiness.
let _adminCache: { userId: string; isAdmin: boolean } | null = null

export async function isAdmin(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return false
  if (_adminCache?.userId === user.id) return _adminCache.isAdmin
  const { data } = await supabase
    .from('admins')
    .select('id')
    .eq('auth_user_id', user.id)
    .maybeSingle()
  const result = !!data
  _adminCache = { userId: user.id, isAdmin: result }
  return result
}

// Clear the cache on any auth change so switching users always gets a fresh check
supabase.auth.onAuthStateChange(() => {
  _adminCache = null
})

/**
 * Returns the current authenticated user's owners.id, or null if not logged in
 * or not in the allowlist.
 */
export async function getCurrentOwnerId(): Promise<string | null> {
  const { data } = await supabase
    .from('owners')
    .select('id')
    .maybeSingle()
  return data?.id ?? null
}

export async function fetchAllowedOwners(): Promise<{ email: string; isAdmin: boolean }[]> {
  const { data, error } = await supabase
    .from('allowed_owners')
    .select('email, is_admin')
    .order('email', { ascending: true })
  if (error) throw new Error(error.message)
  return (data as { email: string; is_admin: boolean }[]).map((row) => ({
    email: row.email,
    isAdmin: row.is_admin,
  }))
}

export async function removeAllowedEmail(email: string): Promise<void> {
  const { error } = await supabase
    .from('allowed_owners')
    .delete()
    .eq('email', email)
  if (error) throw new Error(error.message)
}

export async function addAllowedEmail(email: string, admin: boolean): Promise<void> {
  const { error } = await supabase
    .from('allowed_owners')
    .upsert({ email, is_admin: admin }, { onConflict: 'email' })
  if (error) throw new Error(error.message)
}

// --- Row types returned by Supabase (snake_case, matching DB schema) ---

interface ItemRow {
  id: string
  position: number
  file_type: 'photo' | 'video'
  thumbnail_url: string
  full_url: string
  storage_path: string | null
  created_at: string | null
  camera: string | null
  gps_lat: number | null
  gps_lng: number | null
}

interface CollectionRow {
  id: string
  username: string
  title: string | null
  created_at: string
  date: string | null
  owner_id: string | null
  items: ItemRow[]
}

interface GalleryRow {
  id: string
  short_code: string
  name: string
  landing_image_url: string | null
  landing_image_blur: string | null
  owner_id: string | null
  collections: CollectionRow[]
}

interface GalleryListRow {
  id: string
  name: string
  landing_image_url: string | null
  landing_image_blur: string | null
}

// --- Mapping functions: DB snake_case → frontend camelCase ---

function mapItem(row: ItemRow): Item {
  return {
    id: row.id,
    position: row.position,
    fileType: row.file_type,
    thumbnailUrl: row.thumbnail_url,
    fullUrl: row.full_url,
    storagePath: row.storage_path ?? undefined,
    createdAt: row.created_at,
    metadata:
      row.camera || row.gps_lat != null
        ? {
            camera: row.camera ?? undefined,
            gps:
              row.gps_lat != null && row.gps_lng != null
                ? { lat: row.gps_lat, lng: row.gps_lng }
                : undefined,
          }
        : undefined,
  }
}

function mapCollection(row: CollectionRow): Collection {
  return {
    id: row.id,
    username: row.username,
    title: row.title,
    createdAt: row.created_at,
    date: row.date,
    ownerId: row.owner_id,
    // Sort items by position so they arrive in user-defined order
    items: [...row.items].sort((a, b) => a.position - b.position).map(mapItem),
  }
}

function mapGallery(row: GalleryRow): Gallery {
  return {
    id: row.id,
    shortCode: row.short_code,
    name: row.name,
    landingImageUrl: row.landing_image_url ?? '',
    landingImageBlur: row.landing_image_blur ?? undefined,
    ownerId: row.owner_id ?? null,
    collections: row.collections.map(mapCollection),
  }
}

// --- Public API functions ---

/**
 * Fetch galleries with summary metadata for the Manage page.
 * Admins see all claimed galleries; non-admins see only their own.
 */
export async function fetchGalleriesSummary(options: {
  isAdmin: boolean
  ownerId?: string
}): Promise<GallerySummary[]> {
    let query = supabase
    .from('galleries')
    .select(`
      id,
      short_code,
      name,
      landing_image_url,
      landing_image_blur,
      location_lat,
      location_lng,
      created_at,
      owner_id,
      owners ( email ),
      collections ( id, items ( id ) )
    `)
    .not('claimed_at', 'is', null)
    .order('created_at', { ascending: false })

  if (!options.isAdmin && options.ownerId) {
    // Fetch collaborator gallery IDs so we can include them alongside owned ones
    const { data: collabRows } = await supabase
      .from('gallery_collaborators')
      .select('gallery_id')
      .eq('linked_owner_id', options.ownerId)

    const collaboratorIds: string[] = (collabRows ?? []).map(
      (r: { gallery_id: string }) => r.gallery_id
    )

    // Fetch owned gallery IDs
    const { data: ownedRows } = await supabase
      .from('galleries')
      .select('id')
      .eq('owner_id', options.ownerId)
      .not('claimed_at', 'is', null)

    const ownedIds: string[] = (ownedRows ?? []).map((r: { id: string }) => r.id)

    const allIds = [...ownedIds, ...collaboratorIds]
    if (allIds.length > 0) {
      query = query.in('id', allIds)
    } else {
      // No galleries at all — ensure empty result
      query = query.eq('id', '00000000-0000-0000-0000-000000000000')
    }
  }

  const { data, error } = await query

  if (error) throw new Error(error.message)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data as any[]).map((row) => {
    const collections = (row.collections ?? []) as { id: string; items: { id: string }[] }[]
    return {
      id: row.id,
      shortCode: row.short_code,
      name: row.name ?? '',
      landingImageUrl: row.landing_image_url ?? '',
      landingImageBlur: row.landing_image_blur ?? undefined,
      locationLat: row.location_lat,
      locationLng: row.location_lng,
      createdAt: row.created_at,
      ownerName: row.owners?.email ?? null,
      collectionCount: collections.length,
      itemCount: collections.reduce((sum, c) => sum + c.items.length, 0),
      isOwner: !!options.isAdmin || (!!options.ownerId && row.owner_id === options.ownerId),
    }
  })
}

/**
 * Fetch all galleries (name + cover image only, no collections).
 * Used by Home.tsx to render the gallery list.
 */
export async function fetchGalleries(): Promise<Gallery[]> {
  const { data, error } = await supabase
    .from('galleries')
    .select('id, short_code, name, landing_image_url, landing_image_blur')
    .order('created_at', { ascending: false })

  if (error) throw new Error(error.message)

  return (data as (GalleryListRow & { short_code: string })[]).map((row) => ({
    id: row.id,
    shortCode: row.short_code,
    name: row.name,
    landingImageUrl: row.landing_image_url ?? '',
    landingImageBlur: row.landing_image_blur ?? undefined,
    ownerId: null,
    collections: [],
  }))
}

/**
 * Fetch a single gallery by ID including all collections and items.
 * Used by Gallery.tsx to render the full gallery view.
 */
export async function fetchGallery(galleryId: string): Promise<Gallery> {
  const { data, error } = await supabase
    .from('galleries')
    .select(`
      id,
      short_code,
      name,
      landing_image_url,
      landing_image_blur,
      owner_id,
      collections (
        id,
        username,
        title,
        created_at,
        date,
        owner_id,
        items (
          id,
          position,
          file_type,
          thumbnail_url,
          full_url,
          storage_path,
          created_at,
          camera,
          gps_lat,
          gps_lng
        )
      )
    `)
    .eq('id', galleryId)
    .single()

  if (error) throw new Error(error.message)

  return mapGallery(data as GalleryRow)
}

/**
 * Fetch minimal gallery info (name + cover image) by ID.
 * Used by Gallery.tsx to render the lock screen.
 * claimed is false when claimed_at IS NULL (gallery awaiting a new user).
 */
export async function fetchGalleryInfo(
  galleryId: string
): Promise<{
  name: string
  landingImageUrl: string
  landingImageBlur: string | undefined
  claimed: boolean
  ownerId: string | null
  locationLat: number | null
  locationLng: number | null
}> {
  const { data, error } = await supabase
    .from('galleries')
    .select('name, landing_image_url, landing_image_blur, owner_id, claimed_at, location_lat, location_lng')
    .eq('id', galleryId)
    .single()

  if (error) throw new Error(error.message)

  const row = data as GalleryListRow & {
    owner_id: string | null
    claimed_at: string | null
    location_lat: number | null
    location_lng: number | null
  }
  return {
    name: row.name ?? '',
    landingImageUrl: row.landing_image_url ?? '',
    landingImageBlur: row.landing_image_blur ?? undefined,
    claimed: row.claimed_at !== null,
    ownerId: row.owner_id ?? null,
    locationLat: row.location_lat ?? null,
    locationLng: row.location_lng ?? null,
  }
}

/**
 * Resolve a gallery short code to its UUID.
 * Used by Gallery to look up the UUID when mounted at /:shortCode.
 * Returns null when no gallery matches.
 */
export async function fetchGalleryIdByShortCode(shortCode: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('galleries')
    .select('id')
    .eq('short_code', shortCode)
    .single()

  if (error || !data) return null
  return data.id
}

/**
 * Fetch gallery info directly by short code — combines the short-code lookup
 * and the info query into a single round-trip.
 */
export async function fetchGalleryInfoByShortCode(
  shortCode: string,
): Promise<{
  id: string
  name: string
  landingImageUrl: string
  landingImageBlur: string | undefined
  claimed: boolean
  ownerId: string | null
  locationLat: number | null
  locationLng: number | null
} | null> {
  const { data, error } = await supabase
    .from('galleries')
    .select('id, name, landing_image_url, landing_image_blur, owner_id, claimed_at, location_lat, location_lng')
    .eq('short_code', shortCode)
    .single()

  if (error || !data) return null

  const row = data as GalleryListRow & {
    owner_id: string | null
    claimed_at: string | null
    location_lat: number | null
    location_lng: number | null
  }
  return {
    id: row.id,
    name: row.name ?? '',
    landingImageUrl: row.landing_image_url ?? '',
    landingImageBlur: row.landing_image_blur ?? undefined,
    claimed: row.claimed_at !== null,
    ownerId: row.owner_id ?? null,
    locationLat: row.location_lat ?? null,
    locationLng: row.location_lng ?? null,
  }
}

/**
 * Fetch gallery landing info (short-code lookup + signed cover URL) via edge
 * function. Combines the DB query and the storage signing round-trip into a
 * single network hop so the cover image can start downloading sooner.
 */
export async function fetchGalleryLandingInfo(
  shortCode: string,
): Promise<{
  id: string
  name: string
  coverUrl: string | null
  coverBlur: string | null
  claimed: boolean
  ownerId: string | null
  locationLat: number | null
  locationLng: number | null
} | null> {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-gallery-landing-info`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY as string}`,
      },
      body: JSON.stringify({ shortCode }),
    },
  )

  if (!res.ok) return null

  const data = await res.json() as {
    id: string
    name: string
    coverUrl: string | null
    coverBlur: string | null
    claimed: boolean
    ownerId: string | null
    locationLat: number | null
    locationLng: number | null
  }
  return data
}

/**
 * Claim an unclaimed gallery (claimed_at IS NULL) for the current user.
 * Safe to call multiple times — the WHERE claimed_at IS NULL ensures it only
 * succeeds once.
 */
export async function claimGallery(galleryId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data: ownerData, error: ownerError } = await supabase
    .from('owners')
    .select('id')
    .eq('auth_user_id', user.id)
    .single()

  if (ownerError) throw new Error(ownerError.message)

  const { error } = await supabase
    .from('galleries')
    .update({ owner_id: ownerData.id, claimed_at: new Date().toISOString() })
    .eq('id', galleryId)
    .is('claimed_at', null)

  if (error) throw new Error(error.message)
}

/**
 * Delete a gallery by ID.
 * - Removes all files under {galleryId}/ in storage
 * - Removes the cover image from the public covers bucket
 * - Deletes the gallery row (collections + items cascade automatically)
 */
export async function deleteGallery(galleryId: string): Promise<void> {
  // List and delete all storage files under the gallery's prefix in the private bucket
  const { data: files, error: listError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .list(galleryId, { limit: 1000 })

  if (listError) throw new Error(listError.message)

  if (files && files.length > 0) {
    // Files may be in subfolders (galleryId/collectionId/filename)
    // Recursively collect all paths
    const paths: string[] = []

    for (const file of files) {
      if (file.id) {
        // It's a file
        paths.push(`${galleryId}/${file.name}`)
      } else {
        // It's a folder — list one level deeper
        const { data: subFiles } = await supabase.storage
          .from(STORAGE_BUCKET)
          .list(`${galleryId}/${file.name}`, { limit: 1000 })

        if (subFiles) {
          for (const subFile of subFiles) {
            if (subFile.id) {
              paths.push(`${galleryId}/${file.name}/${subFile.name}`)
            }
          }
        }
      }
    }

    if (paths.length > 0) {
      const { error: removeError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove(paths)

      if (removeError) throw new Error(removeError.message)
    }
  }

  // Also delete the cover image from the public covers bucket
  const { data: coverFiles } = await supabase.storage
    .from(COVERS_BUCKET)
    .list(galleryId, { limit: 1000 })

  if (coverFiles && coverFiles.length > 0) {
    const coverPaths = coverFiles
      .filter((f) => f.id)
      .map((f) => `${galleryId}/${f.name}`)
    if (coverPaths.length > 0) {
      const { error: coverRemoveError } = await supabase.storage
        .from(COVERS_BUCKET)
        .remove(coverPaths)
      if (coverRemoveError) throw new Error(coverRemoveError.message)
    }
  }

  // DB delete — cascades to collections and items
  const { error } = await supabase
    .from('galleries')
    .delete()
    .eq('id', galleryId)

  if (error) throw new Error(error.message)

  metricCount('gallery.deleted', 1)
}

/**
 * Fetch all unclaimed (claimed_at IS NULL) galleries.
 * Admin-only: used to list pending share links on the admin page.
 */
export async function fetchUnclaimedGalleries(): Promise<{ id: string; shortCode: string; createdAt: string; claimDescription: string | null }[]> {
  const { data, error } = await supabase
    .from('galleries')
    .select('id, short_code, created_at, claim_description')
    .is('claimed_at', null)
    .order('created_at', { ascending: false })

  if (error) throw new Error(error.message)

  return (data as { id: string; short_code: string; created_at: string; claim_description: string | null }[]).map((row) => ({
    id: row.id,
    shortCode: row.short_code,
    createdAt: row.created_at,
    claimDescription: row.claim_description ?? null,
  }))
}

/**
 * Create a new unclaimed gallery with no details (owner_id = null, name = '').
 * Admin-only path: creates a bare gallery row and returns the ID so the admin
 * can share the URL with a new user who will claim it via the setup form.
 * An optional description can be set to record who the link is intended for.
 */
export async function createUnclaimedGallery(claimDescription?: string): Promise<{ id: string; shortCode: string }> {
  const { data, error } = await supabase
    .from('galleries')
    .insert({
      owner_id: null,
      name: '',
      location_lat: 0,
      location_lng: 0,
      claim_description: claimDescription?.trim() || null,
    })
    .select('id, short_code')
    .single()

  if (error) throw new Error(error.message)
  metricCount('gallery_share_link.created', 1)
  return { id: data.id, shortCode: data.short_code }
}

/**
 * Create a fully initialized gallery owned by the current user.
 * Used when any logged-in user creates a gallery from the Manage page modal.
 * The gallery is claimed immediately — no separate setup step required.
 */
export async function createOwnedGallery(
  name: string,
  locationLat: number,
  locationLng: number,
  coverImageFile: File,
): Promise<{ id: string; shortCode: string }> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data: ownerData, error: ownerError } = await supabase
    .from('owners')
    .select('id')
    .eq('auth_user_id', user.id)
    .single()

  if (ownerError) throw new Error(ownerError.message)

  // Insert the gallery row first to get an ID.
  // claimed_at is set immediately since this gallery is owned from creation.
  const { data: gallery, error: insertError } = await supabase
    .from('galleries')
    .insert({
      owner_id: ownerData.id,
      name,
      location_lat: locationLat,
      location_lng: locationLng,
      claimed_at: new Date().toISOString(),
    })
    .select('id, short_code')
    .single()

  if (insertError) throw new Error(insertError.message)

  const galleryId = gallery.id
  const shortCode = gallery.short_code

  // Resize cover to max 1920px and generate blur placeholder before uploading
  const resizedCover = await resizeImage(coverImageFile, 1920, 0.82)
  const [blurBase64] = await Promise.all([
    generateBlurBase64(resizedCover),
  ])

  const ext = resizedCover.name.split('.').pop() ?? 'jpg'
  const path = `${galleryId}/cover.${ext}`

  const { error: uploadError } = await supabase.storage
    .from(COVERS_BUCKET)
    .upload(path, resizedCover, { upsert: true })

  if (uploadError) throw new Error(uploadError.message)

  // Update the row with the cover image path and blur placeholder
  const { error: updateError } = await supabase
    .from('galleries')
    .update({ landing_image_url: path, landing_image_blur: blurBase64 || null })
    .eq('id', galleryId)

  if (updateError) throw new Error(updateError.message)

  metricCount('gallery.created', 1)
  return { id: galleryId, shortCode }
}

/**
 * Update gallery metadata (name, location, cover image).
 * Owners and admins can call this — RLS permits it.
 * Only provided fields are updated; omitted fields are left unchanged.
 */
export async function updateGallery(
  galleryId: string,
  options: {
    name?: string
    locationLat?: number
    locationLng?: number
    coverImageFile?: File
  }
): Promise<void> {
  const updateData: Record<string, unknown> = {}

  if (options.name !== undefined) updateData.name = options.name
  if (options.locationLat !== undefined) updateData.location_lat = options.locationLat
  if (options.locationLng !== undefined) updateData.location_lng = options.locationLng

  if (options.coverImageFile) {
    const resizedCover = await resizeImage(options.coverImageFile, 1920, 0.82)
    const blurBase64 = await generateBlurBase64(resizedCover)
    const ext = resizedCover.name.split('.').pop() ?? 'jpg'
    const path = `${galleryId}/cover.${ext}`

    const { error: uploadError } = await supabase.storage
      .from(COVERS_BUCKET)
      .upload(path, resizedCover, { upsert: true })

    if (uploadError) throw new Error(uploadError.message)

    updateData.landing_image_url = path
    if (blurBase64) updateData.landing_image_blur = blurBase64
  }

  if (Object.keys(updateData).length === 0) return

  const { error } = await supabase
    .from('galleries')
    .update(updateData)
    .eq('id', galleryId)

  if (error) throw new Error(error.message)
}

/**
 * Initialize a gallery with name, location, cover image, and owner email.
 * No authentication required — anyone with the gallery URL can claim it.
 *
 * Steps:
 *   1. Upload the cover image to storage (anon policy permits this for unclaimed galleries).
 *   2. Call claim_gallery_anon() RPC which atomically:
 *        - Upserts the email into allowed_owners (so they can register later)
 *        - Sets name, location, cover path, claimed_at, claimed_by_email on the gallery
 *        - Does NOT set owner_id (auto-linked when they later sign up)
 */
export async function initializeGallery(
  galleryId: string,
  ownerEmail: string,
  name: string,
  locationLat: number,
  locationLng: number,
  coverImageFile: File
): Promise<void> {
  // Resize cover to max 1920px then generate blur placeholder before uploading
  // (anon RLS policy allows this for unclaimed galleries)
  const resizedCover = await resizeImage(coverImageFile, 1920, 0.82)
  const blurBase64 = await generateBlurBase64(resizedCover)

  const ext = resizedCover.name.split('.').pop() ?? 'jpg'
  const path = `${galleryId}/cover.${ext}`

  const { error: uploadError } = await supabase.storage
    .from(COVERS_BUCKET)
    .upload(path, resizedCover, { upsert: true })

  if (uploadError) throw new Error(uploadError.message)

  // Atomically claim the gallery and allowlist the email via a SECURITY DEFINER RPC.
  // This works for both authenticated and unauthenticated callers.
  const { error } = await supabase.rpc('claim_gallery_anon', {
    p_gallery_id:  galleryId,
    p_owner_email: ownerEmail,
    p_name:        name,
    p_lat:         locationLat,
    p_lng:         locationLng,
    p_cover_path:  path,
  })

  if (error) throw new Error(error.message)

  // Store the blur placeholder separately (RPC doesn't handle it)
  await supabase
    .from('galleries')
    .update({ landing_image_blur: blurBase64 || null })
    .eq('id', galleryId)

  metricCount('gallery.claimed', 1)
}

/**
 * Upload a collection of files to Supabase Storage and insert the
 * corresponding collection + items rows into the database.
 *
 * Display variants are generated on-the-fly via Supabase image transformations
 * (Pro plan). resolveSignedUrls returns two variants per item:
 *   - thumb:  400×400 cover crop at 75% quality (for the gallery grid)
 *   - full:   1600px wide at 85% quality (for the photo viewer)
 */
export async function uploadCollection(
  galleryId: string,
  username: string,
  files: { file: File; exif: ExifData }[],
  options?: {
    collectionDate?: Date
    title?: string
    /** Pass a geo-authenticated Supabase client for anon uploads. Defaults to the singleton. */
    client?: SupabaseClient
  }
): Promise<Collection> {
  const db = options?.client ?? supabase
  const startTime = performance.now()

  // 1. Insert the collection row first to get its ID.
  // uploaded_at defaults to NOW() in the DB; the user-specified date
  // (if any) goes into the separate date column.
  const collectionInsert: Record<string, unknown> = { gallery_id: galleryId, username }
  if (options?.collectionDate) {
    collectionInsert.date = options.collectionDate.toISOString()
  }
  if (options?.title) {
    collectionInsert.title = options.title
  }

  // Link the collection to the authenticated owner so the uploader is traceable.
  // Skip for geo/anon uploads (options.client is a geo-scoped client with no session).
  if (!options?.client) {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: ownerRow, error: ownerError } = await db
        .from('owners')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle()
      if (ownerError) throw new Error(`Failed to fetch owner row: ${ownerError.message}`)
      if (ownerRow) collectionInsert.owner_id = ownerRow.id
    }
  }

  const { data: collectionData, error: collectionError } = await db
    .from('collections')
    .insert(collectionInsert)
    .select('id, username, title, created_at, date, owner_id')
    .single()

  if (collectionError) throw new Error(collectionError.message)
  const collectionId = collectionData.id

  // 2. Upload each file and build item rows.
  // Files arrive in the user's chosen order (drag-and-drop order from the upload
  // modal, defaulting to EXIF createdAt ascending). We preserve that order by
  // assigning position = index in the files array.
  const itemInserts = await Promise.all(
    files.map(async ({ file, exif }, index) => {
      const resized = await resizeImage(file)
      const ext = resized.name.split('.').pop() ?? 'jpg'
      const path = `${galleryId}/${collectionId}/${crypto.randomUUID()}.${ext}`

      const { error: uploadError } = await db.storage
        .from(STORAGE_BUCKET)
        .upload(path, resized, { upsert: false })

      if (uploadError) throw new Error(uploadError.message)

      const fileType = file.type.startsWith('video/') ? 'video' : 'photo'

      return {
        collection_id: collectionId,
        file_type: fileType,
        // Store the storage path — AuthImage fetches it with auth credentials.
        // thumbnail_url/full_url are left empty for storage-backed items.
        thumbnail_url: '',
        medium_url: '',
        full_url: '',
        storage_path: path,
        // Use pre-extracted EXIF data from the original file.
        created_at: exif.createdAt ?? null,
        // Position reflects the user's chosen order (index in the files array).
        position: index,
        ...(exif.camera ? { camera: exif.camera } : {}),
        ...(exif.gps ? { gps_lat: exif.gps.lat, gps_lng: exif.gps.lng } : {}),
      }
    })
  )

  // 3. Insert all item rows
  const { data: itemsData, error: itemsError } = await db
    .from('items')
    .insert(itemInserts)
    .select('id, position, file_type, thumbnail_url, full_url, storage_path, created_at, camera, gps_lat, gps_lng')

  if (itemsError) throw new Error(itemsError.message)

  // 4. Return the new collection in frontend camelCase shape
  metricCount('collection.uploaded', 1, { count: files.length })
  metricDistribution(
    'collection.upload.duration_ms',
    Math.round(performance.now() - startTime),
    'millisecond',
    { count: files.length },
  )

  return {
    id: collectionId,
    username: collectionData.username,
    title: collectionData.title ?? null,
    createdAt: collectionData.created_at,
    date: collectionData.date ?? null,
    ownerId: collectionData.owner_id ?? null,
    items: (itemsData as ItemRow[]).map(mapItem),
  }
}

/**
 * Generate a signed URL for a single storage path (e.g. gallery cover image).
 * TTL is 1 hour — sufficient for a single browsing session.
 * Returns null if the path is empty or signing fails.
 */
export async function resolveSignedUrl(storagePath: string): Promise<string | null> {
  if (!storagePath) return null
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 3600)
  if (error || !data) return null
  return data.signedUrl
}

export interface SignedUrlVariants {
  thumb: string
  full: string
}

const GRID_TRANSFORM = { width: 400, height: 400, resize: 'cover' as const, quality: 75 }
const VIEWER_TRANSFORM = { width: 1600, quality: 85 }

/**
 * Batch-generate signed URLs for a list of storage paths.
 * Returns a Map<itemId, SignedUrlVariants> with two variants per image item:
 * - thumb: 400×400 cover crop for the gallery grid
 * - full: 1600px wide for the photo viewer
 * Videos get the same signed URL for both variants (no transform).
 * Items using legacy thumbnail_url/full_url seed data are omitted.
 */
export async function resolveSignedUrls(
  items: { id: string; storagePath?: string; fileType: 'photo' | 'video' }[]
): Promise<Map<string, SignedUrlVariants>> {
  const result = new Map<string, SignedUrlVariants>()

  const toSign = items.filter((i) => i.storagePath)
  if (toSign.length === 0) return result

  const videoPaths = new Set(toSign.filter((i) => i.fileType === 'video').map((i) => i.storagePath!))

  const results = await Promise.all(
    toSign.map(async (item) => {
      const path = item.storagePath!
      const isVideo = videoPaths.has(path)

      const [thumbData, fullData] = await Promise.all([
        supabase.storage.from(STORAGE_BUCKET).createSignedUrl(path, 3600,
          isVideo ? undefined : { transform: GRID_TRANSFORM }),
        supabase.storage.from(STORAGE_BUCKET).createSignedUrl(path, 3600,
          isVideo ? undefined : { transform: VIEWER_TRANSFORM }),
      ])

      return {
        id: item.id,
        thumb: thumbData.data?.signedUrl ?? '',
        full: fullData.data?.signedUrl ?? '',
      }
    })
  )

  for (const r of results) {
    result.set(r.id, { thumb: r.thumb, full: r.full })
  }

  return result
}

/**
 * Batch-generate signed URLs for geo-verified anon users via the sign-gallery-urls
 * edge function. Validates the geo token server-side and signs using the service role,
 * bypassing storage RLS which the anon JWT cannot satisfy.
 * Returns a Map<itemId, SignedUrlVariants> with thumb and full variants.
 */
export async function resolveSignedUrlsGeo(
  items: { id: string; storagePath?: string; fileType: 'photo' | 'video' }[],
  galleryId: string,
  geoToken: string,
): Promise<Map<string, SignedUrlVariants>> {
  const result = new Map<string, SignedUrlVariants>()

  const toSign = items.filter((i) => i.storagePath)
  if (toSign.length === 0) return result

  const paths = toSign.map((i) => i.storagePath!)
  const fileTypes = toSign.map((i) => i.fileType)

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sign-gallery-urls`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY as string}`,
      },
      body: JSON.stringify({ galleryId, token: geoToken, paths, fileTypes }),
    },
  )
  if (!res.ok) {
    const text = await res.text().catch(() => res.status.toString())
    throw new Error(`sign-gallery-urls failed (${res.status}): ${text}`)
  }
  const { signedUrls } = await res.json() as { signedUrls: Record<string, { thumb: string; full: string }> }
  for (const item of toSign) {
    const signed = signedUrls[item.storagePath!]
    if (signed) result.set(item.id, signed)
  }

  return result
}

/**
 * Delete a single item from a collection.
 * - Removes the storage file (if any)
 * - Deletes the items row
 * - If the parent collection is now empty, deletes the collection row too
 * Returns true if the parent collection was also deleted.
 */
export async function deleteItem(
  itemId: string,
  collectionId: string,
  storagePath?: string,
): Promise<{ collectionDeleted: boolean }> {
  // Remove storage file if present
  if (storagePath) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([storagePath])
    if (storageError) throw new Error(storageError.message)
  }

  // Delete the item row
  const { error: deleteError } = await supabase
    .from('items')
    .delete()
    .eq('id', itemId)
  if (deleteError) throw new Error(deleteError.message)

  // Check if the collection is now empty
  const { count, error: countError } = await supabase
    .from('items')
    .select('id', { count: 'exact', head: true })
    .eq('collection_id', collectionId)
  if (countError) throw new Error(countError.message)

  if (count === 0) {
    const { error: collDeleteError } = await supabase
      .from('collections')
      .delete()
      .eq('id', collectionId)
    if (collDeleteError) throw new Error(collDeleteError.message)
    metricCount('collection.deleted', 1)
    return { collectionDeleted: true }
  }

  metricCount('item.deleted', 1)
  return { collectionDeleted: false }
}

/**
 * Delete an entire collection and all its items.
 * - Removes all storage files for the collection's items
 * - Deletes the collections row (items cascade automatically in DB)
 */
export async function deleteCollection(
  collectionId: string,
  storagePaths: string[],
): Promise<void> {
  // Remove all storage files in parallel
  if (storagePaths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove(storagePaths)
    if (storageError) throw new Error(storageError.message)
  }

  // Delete the collection row — items cascade
  const { error } = await supabase
    .from('collections')
    .delete()
    .eq('id', collectionId)
  if (error) throw new Error(error.message)
  metricCount('collection.deleted', 1, { count: storagePaths.length })
}

/**
 * Add a collaborator (by email) to a gallery.
 * Gallery owner or existing collaborator can call this — enforced by the RPC.
 */
export async function addGalleryCollaborator(
  galleryId: string,
  email: string,
): Promise<void> {
  const { error } = await supabase.rpc('add_gallery_collaborator', {
    p_gallery_id: galleryId,
    p_email: email,
  })
  if (error) throw new Error(error.message)
  metricCount('collaborator.added', 1)
}

/**
 * Remove a collaborator (by email) from a gallery.
 * Gallery owner or existing collaborator can call this — enforced by the RPC.
 */
export async function removeGalleryCollaborator(
  galleryId: string,
  email: string,
): Promise<void> {
  const { error } = await supabase.rpc('remove_gallery_collaborator', {
    p_gallery_id: galleryId,
    p_email: email,
  })
  if (error) throw new Error(error.message)
  metricCount('collaborator.removed', 1)
}

/**
 * Fetch all collaborators for a gallery.
 * Accessible to the gallery owner and the collaborators themselves.
 */
export async function fetchGalleryCollaborators(
  galleryId: string,
): Promise<Collaborator[]> {
  const { data, error } = await supabase
    .from('gallery_collaborators')
    .select('email, linked_owner_id, created_at')
    .eq('gallery_id', galleryId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(error.message)

  return (data as { email: string; linked_owner_id: string | null; created_at: string }[]).map(
    (row) => ({
      email: row.email,
      linkedOwnerId: row.linked_owner_id ?? null,
      createdAt: row.created_at,
    }),
  )
}
